import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    AssetType,
    defaultValueFor,
} from '@components';
import {
    AssetScope,
    IAsset,
    IAssetRobot,
    IAssetType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { SessionsService } from '@services';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest';

import cloneDeep from 'lodash-es/cloneDeep';
import { Subject } from 'rxjs';

import {
    AssetCredentialValidator,
    getValueFor,
    resetForm,
    RobotAssetSource,
    RobotDuplicateValidator,
    RobotValidator,
    validatorsFor,
} from '../../utils';

@Component({
    selector: 'ui-robot-value-editor',
    templateUrl: './ui-robot-value-editor.component.html',
    styleUrls: ['./ui-robot-value-editor.component.scss'],
    providers: [SessionsService],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiRobotValueEditorComponent implements OnInit, OnDestroy {
    @Input()
    public set type(type: IAssetType) {
        this._type = type;
        if (this.formGroup && this.formGroup.get('robotValue')) {
            this.robotAssets.updateType(type);
            this.editedRobot = this.robotAssets.find(this.editedRobot)!;
            resetForm(this.formGroup, 'robotValue', this._type, this.isNewEntry, this._readonly);
        }
    }
    public get type(): IAssetType {
        return this._type;
    }

    public get selectedRobot() {
        return this.editedRobot == null ||
            this.editedRobot.robotId === -1 ? [] : [{
                id: this.editedRobot.robotId,
                text: this.editedRobot.robotName,
            }];
    }

    public get isEditorOpen() {
        return this.editedRobot != null;
    }

    public get isEditorValid() {
        return this.robotAssets.isCollectionValid(this.type, this._readonly);
    }

    public get hasRobots() {
        return this.robotAssets.length > 0;
    }

    @Input()
    public typeLabel!: string;

    @Input()
    public asset!: IAsset;

    @Input()
    public isNewEntry!: boolean;

    @Input()
    public default!: boolean;

    @Input()
    public set credentialStoreId(value: number | undefined) {
        this._credentialStoreId = value;
        if (this._type === 'Credential' && this.formGroup && this.formGroup.get('robotValue')) {
            this.robotAssets.updateCredentialStore(value);
            this.editedRobot = this.robotAssets.find(this.editedRobot)!;
        }
    }
    public get credentialStoreId() {
        return this._credentialStoreId;
    }

    @Input()
    public credentialStoreChange$!: Subject<void>;

    @Input()
    public set readonly(readonly: boolean | undefined) {
        this._readonly = readonly;
        if (this._type === 'Credential' && this.formGroup && this.formGroup.get('robotValue')) {
            resetForm(this.formGroup, 'robotValue', this._type, this.isNewEntry, readonly, true);
        }
    }
    public get readonly() {
        return this._readonly;
    }

    public get AssetScope() {
        return AssetScope;
    }

    public editedRobot?: IAssetRobot;

    public robotAssets!: RobotAssetSource;
    public formGroup!: FormGroup;

    public robotSearchFactory = this._sessionService.suggest;

    private _type!: IAssetType;
    private _credentialStoreId?: number;
    private _readonly?: boolean;

    constructor(
        private _fb: FormBuilder,
        private _sessionService: SessionsService,
        private _translateService: TranslateService,
    ) { }

    ngOnInit() {
        this.robotAssets = new RobotAssetSource(cloneDeep(this.asset.robotValues));

        this.formGroup = this._fb.group(
            {
                'robot': [
                    this.selectedRobot,
                    Validators.compose([
                        RobotValidator,
                        RobotDuplicateValidator(this.robotAssets),
                    ]),
                ],
                'robotValue': [
                    defaultValueFor(this.type, null, this.readonly), validatorsFor(this.type, true, this.readonly, true),
                ],
            },
            {
                validator: AssetCredentialValidator('robotValue', () => this.type, () => this.editedRobot!, () => this.readonly),
            },
        );

        this.credentialStoreChange$.subscribe(_ => {
            if (this._type === 'Credential' && this.formGroup && this.formGroup.get('robotValue')) {
                resetForm(this.formGroup, 'robotValue', this._type, this.isNewEntry, this._readonly, true);
                this.robotAssets.updateCredentialStore(this.asset.credentialStoreId);
            }
        });
    }

    ngOnDestroy() {
        this.credentialStoreChange$.complete();
    }

    public addEntry() {
        this.expandRow(this.robotAssets.generateEntry(this.type, this._credentialStoreId));
        this.formGroup.get('robot')!.reset([]);
        this.robotAssets.update();
    }

    public expandRow(entity: IAssetRobot) {
        resetForm(this.formGroup, 'robotValue', this._type, entity.id < 0, this._readonly, true);
        if (
            this.editedRobot &&
            this.editedRobot.id === -1
        ) {
            this.robotAssets.remove(this.editedRobot);
        }
        this.editedRobot = entity;
        if (this._type === 'Credential') {
            this.editedRobot.credentialStoreId = this._credentialStoreId;
        }
        this.formGroup.get('robotValue')!.patchValue(getValueFor(this.editedRobot, this._readonly));
        this.formGroup.markAsUntouched();
    }

    public removeRow(entity: IAssetRobot) {
        this.robotAssets.remove(entity);
    }

    public isValid(entry: IAssetRobot) {
        switch (entry.type) {
            case AssetType.Credential:
                if (this._readonly) {
                    break;
                }
                return !!entry.credentialUsername;
            case AssetType.Bool:
                return entry.boolValue != null;
        }
        const errors = validatorsFor(entry.type, true, this._readonly, true)!({
            value: entry.stringValue || entry.intValue || entry.externalName,
        } as FormControl);
        return errors === null;
    }

    public getValueFor(asset: IAssetRobot) {
        switch (asset.type) {
            case AssetType.Bool:
                return asset.boolValue ?
                    this._translateService.instant('assets_modal_form_bool_true') :
                    this._translateService.instant('assets_modal_form_bool_false');
            case AssetType.Integer:
                return asset.intValue;
            case AssetType.Text:
                return asset.stringValue;
            case AssetType.Credential:
                if (this._readonly) {
                    return asset.externalName;
                }
                return `${asset.credentialUsername}:****`;
        }
    }

    public onRobotChange(ev: ISuggestValue) {
        ev = ev || {} as ISuggestValue;
        if (!this.editedRobot) { return; }
        if (
            this.editedRobot.id > 0 &&
            this.editedRobot.robotId !== ev.id
        ) {
            // if the robot was changed on an existing entry
            // clear the initial username to avoid username has changed validation
            this.editedRobot.credentialUsername = null;
            this.formGroup.get('robotValue')!.reset(defaultValueFor(this.type, null, this._readonly));
        }
        this.editedRobot.robotName = ev.text;
        this.editedRobot.robotId = Number(ev.id) || -1;
    }

    public onCancel() {
        if (!this.editedRobot) { return; }
        if (this.editedRobot.id === -1) {
            this.robotAssets.remove(this.editedRobot);
        }
        this.editedRobot = undefined;
    }

    public onSave() {
        if (!this.editedRobot) { return; }
        if (
            this.formGroup.invalid ||
            this.editedRobot.robotId === -1
        ) { return; }

        this.robotAssets.persist(
            this.editedRobot,
            this.type,
            this.formGroup.get('robotValue')!.value,
            this.readonly,
        );

        this.editedRobot = undefined;
    }
}
